document.documentElement.classList.remove('no-js');

const q = (s, c=document) => c.querySelector(s);
const qa = (s, c=document) => [...c.querySelectorAll(s)];

const optionHandle = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const mediaMatchesColor = (element, colorKey, filterByColor) => {
  if (!filterByColor) return true;
  const colorTokens = element.dataset.mediaColors || '|';
  return colorTokens === '|' || colorTokens.includes(`|${colorKey}|`);
};

const activateProductMedia = (gallery, mediaId) => {
  if (!gallery || !mediaId) return;
  const panels = qa('[data-gallery-panel]', gallery);
  const thumbnails = qa('[data-gallery-thumbnail]', gallery);
  const targetPanel = panels.find(panel => panel.dataset.mediaId === String(mediaId));
  if (!targetPanel) return;

  const emptyPanel = q('[data-gallery-empty]', gallery);
  if (emptyPanel) emptyPanel.hidden = true;

  panels.forEach(panel => {
    const active = panel === targetPanel;
    panel.hidden = !active;
    panel.classList.toggle('is-active', active);
    if (!active) q('video', panel)?.pause();
  });

  thumbnails.forEach(thumbnail => {
    const active = thumbnail.dataset.mediaId === String(mediaId);
    thumbnail.classList.toggle('is-active', active);
    thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
  });
};

const filterProductGallery = (gallery, colorValue, preferredMediaId = '') => {
  if (!gallery) return;
  const filterByColor = gallery.dataset.filterByColor === 'true';
  const colorKey = optionHandle(colorValue);
  const panels = qa('[data-gallery-panel]', gallery);
  const thumbnails = qa('[data-gallery-thumbnail]', gallery);
  const emptyPanel = q('[data-gallery-empty]', gallery);
  const visiblePanels = panels.filter(panel => mediaMatchesColor(panel, colorKey, filterByColor));
  const visibleMediaIds = new Set(visiblePanels.map(panel => panel.dataset.mediaId));

  thumbnails.forEach(thumbnail => {
    const visible = visibleMediaIds.has(thumbnail.dataset.mediaId);
    thumbnail.hidden = !visible;
    thumbnail.setAttribute('aria-hidden', visible ? 'false' : 'true');
  });

  if (!visiblePanels.length) {
    panels.forEach(panel => {
      panel.hidden = true;
      panel.classList.remove('is-active');
      q('video', panel)?.pause();
    });
    thumbnails.forEach(thumbnail => {
      thumbnail.classList.remove('is-active');
      thumbnail.setAttribute('aria-current', 'false');
    });
    if (emptyPanel) emptyPanel.hidden = false;
    gallery.dataset.activeColor = colorKey;
    return;
  }

  const preferredPanel = visiblePanels.find(panel => panel.dataset.mediaId === String(preferredMediaId));
  const colorSpecificPanel = visiblePanels.find(panel => (panel.dataset.mediaColors || '|') !== '|');
  const currentPanel = visiblePanels.find(panel => panel.classList.contains('is-active'));
  const colorChanged = gallery.dataset.activeColor !== colorKey;
  const targetPanel = preferredPanel || (colorChanged ? colorSpecificPanel : currentPanel) || colorSpecificPanel || visiblePanels[0];
  activateProductMedia(gallery, targetPanel.dataset.mediaId);
  gallery.dataset.activeColor = colorKey;
};

qa('[data-product-gallery]').forEach(gallery => {
  qa('[data-gallery-thumbnail]', gallery).forEach(thumbnail => {
    thumbnail.addEventListener('click', () => {
      activateProductMedia(gallery, thumbnail.dataset.mediaId);
    });
  });
  filterProductGallery(gallery, gallery.dataset.selectedColor, gallery.dataset.featuredMediaId);
});

q('[data-menu-toggle]')?.addEventListener('click', (event) => {
  const header = event.currentTarget.closest('[data-header]');
  const open = header.classList.toggle('mobile-open');
  event.currentTarget.setAttribute('aria-expanded', open ? 'true' : 'false');
});

const renderVariantPrice = (priceNode, variantOption) => {
  if (!priceNode || !variantOption?.dataset.price) return;
  const price = variantOption.dataset.price;
  const comparePrice = variantOption.dataset.comparePrice;
  const salePrice = variantOption.dataset.salePrice;
  const salePreview = variantOption.dataset.salePreview === 'true';
  const addPricePart = (tag, className, value) => {
    const part = document.createElement(tag);
    part.className = className;
    part.textContent = value;
    priceNode.append(part);
  };

  priceNode.replaceChildren();
  if (salePreview && salePrice) {
    addPricePart('s', 'price__original', price);
    addPricePart('span', 'price__sale', salePrice);
  } else {
    addPricePart('span', 'price__current', price);
    if (comparePrice && comparePrice !== price) addPricePart('s', 'price__compare', comparePrice);
  }
};

qa('[data-variant-select]').forEach(select => {
  const form = select.closest('[data-product-form]');
  if (!form) return;

  const picker = q('[data-product-options]', form);
  const gallery = q('[data-product-gallery]', form.closest('.product-main'));
  const priceNode = q('[data-product-price]', form.closest('.product-info'));
  const addButton = q('[data-add-to-cart]', form);
  const status = q('[data-variant-status]', form);
  const pickerButtons = picker ? qa('[data-option-value]', picker) : [];
  const primaryOptionIndex = Number(picker?.dataset.primaryOptionIndex || 0);
  const variants = [...select.options].map(option => {
    let options = [];
    try {
      options = JSON.parse(option.dataset.options || '[]');
    } catch (error) {
      options = [];
    }
    return {
      id: option.value,
      options,
      available: option.dataset.available === 'true',
      element: option
    };
  });

  const currentVariant = () => variants.find(variant => variant.id === select.value) || variants[0];

  const syncPickerSelection = variant => {
    pickerButtons.forEach(button => {
      const optionIndex = Number(button.dataset.optionIndex);
      const selected = variant?.options[optionIndex] === button.dataset.optionValue;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    qa('[data-selected-option]', picker).forEach(label => {
      const optionIndex = Number(label.dataset.selectedOption);
      label.textContent = variant?.options[optionIndex] || '';
    });
  };

  const updateOptionAvailability = variant => {
    pickerButtons.forEach(button => {
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      const available = variants.some(candidate => {
        if (!candidate.available || candidate.options[optionIndex] !== optionValue) return false;
        if (optionIndex === primaryOptionIndex) return true;
        return candidate.options[primaryOptionIndex] === variant?.options[primaryOptionIndex];
      });
      button.disabled = !available;
      button.classList.toggle('is-unavailable', !available);
      button.setAttribute('aria-disabled', available ? 'false' : 'true');
    });
  };

  const renderVariant = (variant, updateUrl = false) => {
    const variantOption = variant?.element;
    const available = Boolean(variant?.available);
    renderVariantPrice(priceNode, variantOption);
    syncPickerSelection(variant);
    updateOptionAvailability(variant);

    if (addButton) {
      addButton.disabled = !available;
      addButton.textContent = available ? addButton.dataset.addLabel : addButton.dataset.soldLabel;
    }
    if (status) status.hidden = available;

    const featuredMediaId = variantOption?.dataset.featuredMediaId;
    filterProductGallery(gallery, variant?.options[primaryOptionIndex], featuredMediaId);

    if (updateUrl && variant?.id && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  };

  pickerButtons.forEach(button => {
    button.addEventListener('click', () => {
      if (button.disabled) return;
      const activeVariant = currentVariant();
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      const desiredOptions = [...(activeVariant?.options || [])];
      desiredOptions[optionIndex] = optionValue;

      let nextVariant = variants.find(variant =>
        variant.available && variant.options.every((value, index) => value === desiredOptions[index])
      );

      if (!nextVariant) {
        nextVariant = variants.find(variant =>
          variant.available &&
          variant.options[primaryOptionIndex] === desiredOptions[primaryOptionIndex] &&
          variant.options[optionIndex] === optionValue
        );
      }

      if (!nextVariant) return;
      select.value = nextVariant.id;
      select.dispatchEvent(new Event('change', {bubbles: true}));
    });
  });

  select.addEventListener('change', () => renderVariant(currentVariant(), true));
  renderVariant(currentVariant());
});

if ('IntersectionObserver' in window && document.body.dataset.reveal !== 'off') {
  const items = qa('[data-reveal]');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {threshold: .08});
  items.forEach(item => observer.observe(item));
}
